import {
  AmbientLight,
  Box3,
  BufferGeometry,
  Camera,
  Color,
  DirectionalLight,
  DoubleSide,
  Float32BufferAttribute,
  Mesh,
  MeshLambertMaterial,
  Scene,
  Uint32BufferAttribute,
  Vector3,
  WebGLRenderer,
} from "three";
import { Observable, observe } from "../../utils/observe";
import { Viewer } from "../viewer";

@Observable
export class DesignManager {
  ifcLoader: Worker = null as any;
  ifcLoaderReady = false;

  scene: Scene = null as any;
  batchedMesh: Mesh | null = null;
  loaderData: any = null;
  groupBoundingBox: Box3 | null = null;
  meshBasePosition: Vector3 = new Vector3();

  constructor(private viewer: Viewer) {}

  setScene(scene: Scene) {
    this.scene = scene;
  }

  init() {
    if (!this.scene) {
      this.scene = new Scene();
    }
    const { viewer } = this;

    try {
      this.ifcLoader = new Worker("/ifc-load-worker.js");
    } catch (e: any) {
      console.error("[DesignManager] Failed to create Worker:", e.message);
      this.viewer.designStatus = "error";
      return;
    }

    // Setup lights
    const directionalLight = new DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, -1, 0);
    this.scene.add(directionalLight);
    const secondirectionalLight = new DirectionalLight(0xffffff, 1);
    secondirectionalLight.position.set(1, 1, 1);
    this.scene.add(secondirectionalLight);

    const ambientLight = new AmbientLight(0xffffff, 2);
    this.scene.add(ambientLight);

    // Worker message handler
    this.ifcLoader.onmessage = ({ data }) => {
      if (data.type === "ready") {
        this.ifcLoaderReady = true;
        return;
      } else if (data.type === "error") {
        console.error("[DesignManager] Worker error:", data.error);
        this.viewer.designStatus = "error";
        return;
      } else {
        if (!!data.data) {
          this.viewer.designStatus = "loaded";
          this.loaderData = data.data;
        } else if (data.error) {
          console.error("[DesignManager] Worker returned error:", data.error);
          this.viewer.designStatus = "error";
        }
      }
    };

    // Worker error handler
    this.ifcLoader.onerror = (error) => {
      console.error("[DesignManager] Worker.onerror:", error);
      this.viewer.designStatus = "error";
    };

    // Setup observers
    observe(this, ["loaderData"], () => {
      this.build();
    });

    observe(
      viewer,
      ["designOffsetMode", "designBoundOffset", "cloudPointOffset"],
      () => {
        this.syncOffset();
      },
    );

    observe(this, ["batchedMesh"], () => {
      this.syncOffset();
      this.calculateBoundOffset();
      viewer.MainRender();
    });

    observe(viewer, ["designURL"], this.handleDesignURLChange);
    observe(this, ["ifcLoaderReady"], this.handleDesignURLChange);

    observe(
      viewer,
      [
        "cloudPointOffset",
        "designBoundOffset",
        "designOffsetMode",
        "designStatus",
      ],
      () => {
        viewer.updateRenderer();
        viewer.MainRender();
      },
    );
    observe(this, ["batchedMesh"], () => {
      viewer.updateRenderer();
      viewer.MainRender();
    });
    observe(viewer, ["designVisible"], () => {
      if (this.batchedMesh) {
        this.batchedMesh.visible = viewer.designVisible;
        viewer.updateRenderer();
        viewer.MainRender();
      }
    });
  }
  handleDesignURLChange = () => {
    if (!this.ifcLoaderReady) {
      return;
    }
    this.unload();
    if (this.viewer.designURL) {
      this.viewer.designStatus = "loading";
      try {
        this.ifcLoader.postMessage({ url: this.viewer.designURL });
      } catch (e: any) {
        console.error("[DesignManager] postMessage failed:", e.message);
        this.viewer.designStatus = "error";
      }
    }
  };
  syncOffset() {
    const offset =
      this.viewer.designOffsetMode === "cloudpoint"
        ? this.viewer.cloudPointOffset
        : this.viewer.designBoundOffset;

    if (this.batchedMesh) {
      this.batchedMesh.position.set(
        offset[0] + this.meshBasePosition.x,
        offset[1] + this.meshBasePosition.y,
        offset[2] + this.meshBasePosition.z,
      );
    }
  }

  build() {
    if (!this.loaderData) return;

    const meshData = this.loaderData as {
      vertices: Float32Array;
      normals: Float32Array;
      indices: Uint32Array;
      translation: [number, number, number];
      color: { r: number; g: number; b: number; a: number };
    }[];

    // Calculate totals for merged geometry
    let totalVertices = 0;
    let totalIndices = 0;
    for (const data of meshData) {
      totalVertices += data.vertices.length / 3;
      totalIndices += data.indices.length;
    }

    // Pre-allocate merged arrays
    const mergedPositions = new Float32Array(totalVertices * 3);
    const mergedNormals = new Float32Array(totalVertices * 3);
    const mergedColors = new Float32Array(totalVertices * 3);
    const mergedIndices = new Uint32Array(totalIndices);

    let vertexOffset = 0;
    let indexOffset = 0;

    const minBrightness = 0.4;

    // Use first mesh's translation as base offset (Float64 precision)
    const baseTranslation = meshData[0].translation || [0, 0, 0];

    for (let i = 0; i < meshData.length; i++) {
      const data = meshData[i];
      const vertexCount = data.vertices.length / 3;
      const t = data.translation || [0, 0, 0];

      // Copy positions, applying relative translation delta for non-base meshes
      const dx = t[0] - baseTranslation[0];
      const dy = t[1] - baseTranslation[1];
      const dz = t[2] - baseTranslation[2];

      if (dx === 0 && dy === 0 && dz === 0) {
        mergedPositions.set(data.vertices, vertexOffset * 3);
      } else {
        for (let k = 0; k < vertexCount; k++) {
          mergedPositions[(vertexOffset + k) * 3] = data.vertices[k * 3] + dx;
          mergedPositions[(vertexOffset + k) * 3 + 1] =
            data.vertices[k * 3 + 1] + dy;
          mergedPositions[(vertexOffset + k) * 3 + 2] =
            data.vertices[k * 3 + 2] + dz;
        }
      }
      mergedNormals.set(data.normals, vertexOffset * 3);

      // Copy indices with offset
      for (let j = 0; j < data.indices.length; j++) {
        mergedIndices[indexOffset + j] = data.indices[j] + vertexOffset;
      }

      // Set vertex colors
      const hasValidColor =
        data.color &&
        typeof data.color.r === "number" &&
        typeof data.color.g === "number" &&
        typeof data.color.b === "number";

      const r = Math.max(hasValidColor ? data.color.r : 0.5, minBrightness);
      const g = Math.max(hasValidColor ? data.color.g : 0.5, minBrightness);
      const b = Math.max(hasValidColor ? data.color.b : 0.5, minBrightness);

      for (let j = 0; j < vertexCount; j++) {
        mergedColors[(vertexOffset + j) * 3] = r;
        mergedColors[(vertexOffset + j) * 3 + 1] = g;
        mergedColors[(vertexOffset + j) * 3 + 2] = b;
      }

      vertexOffset += vertexCount;
      indexOffset += data.indices.length;
    }

    // Create single merged geometry
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new Float32BufferAttribute(mergedPositions, 3),
    );
    geometry.setAttribute(
      "normal",
      new Float32BufferAttribute(mergedNormals, 3),
    );
    geometry.setAttribute("color", new Float32BufferAttribute(mergedColors, 3));
    geometry.setIndex(new Uint32BufferAttribute(mergedIndices, 1));

    // Rotate geometry (IFC Y-up to Z-up)
    geometry.rotateX(Math.PI / 2);
    geometry.computeBoundingBox();

    const material = new MeshLambertMaterial({
      vertexColors: true,
      side: DoubleSide,
      transparent: true,
      opacity: 0.7,
      depthWrite: true,
    });

    const mesh = new Mesh(geometry, material);

    // Apply base translation to mesh position (Float64 precision)
    // After rotateX(PI/2): IFC(x,y,z) -> Three(x,-z,y)
    this.meshBasePosition = new Vector3(
      baseTranslation[0],
      -baseTranslation[2],
      baseTranslation[1],
    );
    mesh.position.copy(this.meshBasePosition);

    const overallBox = geometry.boundingBox || new Box3();

    this.scene.add(mesh);
    this.groupBoundingBox = overallBox;
    this.batchedMesh = mesh;

    const centerX =
      (overallBox.min.x + overallBox.max.x) / 2 + this.meshBasePosition.x;
    const centerY =
      (overallBox.min.y + overallBox.max.y) / 2 + this.meshBasePosition.y;
    const centerZ =
      (overallBox.min.z + overallBox.max.z) / 2 + this.meshBasePosition.z;
    this.viewer.designBoundOffset = [-centerX, -centerY, -centerZ];

    this.syncOffset();
  }

  unload() {
    if (this.batchedMesh) {
      this.batchedMesh.geometry.dispose();
      if (this.batchedMesh.material instanceof MeshLambertMaterial) {
        this.batchedMesh.material.dispose();
      }
      this.scene.remove(this.batchedMesh);
      this.batchedMesh = null;
      this.groupBoundingBox = null;
      this.viewer.designStatus = "blank";
    }
  }

  calculateBoundOffset() {
    if (!this.groupBoundingBox) return;
    const bbox = this.groupBoundingBox;
    const centerX = (bbox.min.x + bbox.max.x) / 2 + this.meshBasePosition.x;
    const centerY = (bbox.min.y + bbox.max.y) / 2 + this.meshBasePosition.y;
    const centerZ = (bbox.min.z + bbox.max.z) / 2 + this.meshBasePosition.z;
    this.viewer.designBoundOffset = [-centerX, -centerY, -centerZ];
  }

  private _viewDir = new Vector3();
  private _toCorner = new Vector3();

  private computeAdaptiveNearFar(
    camera: Camera,
  ): { near: number; far: number } | null {
    if (!this.batchedMesh || !this.groupBoundingBox) return null;

    // World-space bounding box = geometry bbox + mesh position offset
    const worldBox = this.groupBoundingBox
      .clone()
      .translate(this.batchedMesh.position);

    // Camera view direction (points into scene, i.e. -Z in camera local space)
    const viewDir = camera.getWorldDirection(this._viewDir);
    const cameraPos = camera.position;

    // Project all 8 corners onto view direction to get signed depth
    // depth = (corner - cameraPos) . viewDir
    const xs = [worldBox.min.x, worldBox.max.x];
    const ys = [worldBox.min.y, worldBox.max.y];
    const zs = [worldBox.min.z, worldBox.max.z];

    let minDepth = Infinity;
    let maxDepth = -Infinity;
    for (const x of xs) {
      for (const y of ys) {
        for (const z of zs) {
          this._toCorner.set(x - cameraPos.x, y - cameraPos.y, z - cameraPos.z);
          const depth = this._toCorner.dot(viewDir);
          if (depth < minDepth) minDepth = depth;
          if (depth > maxDepth) maxDepth = depth;
        }
      }
    }

    // If entire box is behind camera, skip adaptive clipping
    if (maxDepth <= 0) return null;

    // Ortho: allow negative near (no perspective divide, "behind camera" is valid)
    // Perspective: near must be > 0
    const isOrtho = !!(camera as any).isOrthographicCamera;
    const near = isOrtho ? minDepth : Math.max(minDepth, 0.01);
    const far = Math.max(maxDepth, near + 0.01);

    return { near, far };
  }

  render(renderer: WebGLRenderer, camera: Camera) {
    if (!this.scene) return;
    // Clear only depth buffer before rendering design scene
    // This fixes iOS Safari issue where depth from previous scenes blocks this scene
    renderer.clearDepth();
    renderer.render(this.scene, camera);
  }
}
